import { Checkbox, DatePicker, Select } from "@caprikorn/dynamo-ui";
import { addBusinessDays, isWeekend } from "date-fns";
import type { FC } from "react";
import { Controller } from "react-hook-form";

import { POTENCIES } from "@services/services.constants";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PotencyForm.module.css";
import type { PotencyFormProps } from "./PotencyForm.types";

const MIN_POTENCY_DATE = 3;

export const PotencyForm: FC<PotencyFormProps> = ({ control }) => {
  const { t } = useCustomTranslation(["main", "common"]);

  return (
    <div className={classes.container} data-testid="potencyForm-RMFormWrapper">
      <Controller
        control={control}
        name="potencyForm.potency"
        render={({ field, fieldState }) => (
          <Select
            label={t("requestDrawer.steps.potency.potency")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value || null}
          >
            <Select.Trigger
              data-testid="PotencieSelector"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
            />
            <Select.Dropdown data-testid="DropDown-List">
              <Select.Options>
                {POTENCIES?.map((option) => (
                  <Select.Option key={option} value={option} />
                ))}
              </Select.Options>
            </Select.Dropdown>
          </Select>
        )}
        rules={{ required: "form.error.required" }}
      />
      <Controller
        control={control}
        name="potencyForm.tariffOption"
        render={({ field, fieldState }) => (
          <Select
            label={t("requestDrawer.steps.potency.tariffOption")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value || null}
          >
            <Select.Trigger
              data-testid="PriceOptionSelector"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
            />
            <Select.Dropdown data-testid="DropDown-List">
              <Select.Options>
                <Select.Option
                  label={t("requestDrawer.steps.potency.tariffOptionBase")}
                  value="BASE"
                />
                <Select.Option
                  label={t("requestDrawer.steps.potency.tariffOption2dials")}
                  value="HP/HC"
                />
                <Select.Option
                  label={t("requestDrawer.steps.potency.tariffOption4dials")}
                  value="HCE/HPE/HPH/HCH"
                />
              </Select.Options>
            </Select.Dropdown>
          </Select>
        )}
        rules={{ required: "form.error.required" }}
      />
      <Controller
        control={control}
        name="potencyForm.dueDate"
        render={({ field, fieldState }) => (
          <DatePicker
            disabledDate={isWeekend}
            label={t("requestDrawer.steps.potency.date")}
            minValue={addBusinessDays(
              new Date(),
              MIN_POTENCY_DATE - 1,
            ).toISOString()}
            onBlur={field.onBlur}
            onChange={(values) => field.onChange(values[0])}
            value={[field.value]}
          >
            <DatePicker.Trigger
              data-testid="potency-date-picker"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
            />
            <DatePicker.Dropdown />
          </DatePicker>
        )}
        rules={{ required: "form.error.required" }}
      />
      <Controller
        control={control}
        name="potencyForm.onlyOneContact"
        render={({ field }) => (
          <Checkbox
            checked={Boolean(field.value)}
            data-testid="checkbox-potencyChange"
            label={t("requestDrawer.steps.potency.isContactPresent")}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  );
};
